// Waterfall engine — when the operating-account balance projects to
// negative at the end of a month, walk the configured waterfall and
// pull cash from each source until the deficit is covered (or all
// sources are exhausted, which is its own alert).
//
// Sources are either bank accounts (sweep available cash) or term
// deposits (break early — flagged as an alert because it forfeits
// some interest).

import type {
  CashflowBankAccount,
  CashflowTermDeposit,
  CashflowWaterfallStep,
} from '../../cashflowTypes';

export type DrawdownStep = {
  sourceId: string;
  sourceKind: 'account' | 'term-deposit';
  amount: number;
  /** Only for TDs: the maturity date the user is breaking. */
  brokenEarlyMaturity?: string;
  /** Only for TDs: rough lost-interest estimate (rateAnnual * principal
   *  * fraction-of-year remaining). */
  brokenEarlyLostInterest?: number;
};

export type WaterfallResult = {
  /** Per-source draws applied this month, in waterfall order. */
  steps: DrawdownStep[];
  /** Remaining deficit AFTER walking the entire waterfall; non-zero
   *  means the diocese genuinely runs out of cash this month. */
  uncovered: number;
};

/** Sorted-by-displayOrder copy of the waterfall, ready to walk. */
export function sortWaterfall(steps: CashflowWaterfallStep[]): CashflowWaterfallStep[] {
  return [...steps].sort((a, b) => a.displayOrder - b.displayOrder);
}

/** Apply the waterfall to cover a `deficit` in the given month.
 *  `availableByAccount` and `availableByTd` are mutable maps of
 *  cash currently sittable in each source — the function deducts as
 *  it goes. */
export function applyWaterfall(
  deficit: number,
  ym: string,
  waterfall: CashflowWaterfallStep[],
  accountsById: Map<string, CashflowBankAccount>,
  tdsById: Map<string, CashflowTermDeposit>,
  availableByAccount: Map<string, number>,
  availableByTd: Map<string, number>,
): WaterfallResult {
  if (deficit <= 0) return { steps: [], uncovered: 0 };
  const sorted = sortWaterfall(waterfall);
  const steps: DrawdownStep[] = [];
  let remaining = deficit;
  for (const step of sorted) {
    if (remaining <= 0) break;
    if (step.sourceKind === 'account') {
      const acc = accountsById.get(step.sourceId);
      if (!acc) continue;
      const have = Math.max(0, availableByAccount.get(step.sourceId) ?? 0);
      if (have <= 0) continue;
      const take = Math.min(have, remaining);
      availableByAccount.set(step.sourceId, have - take);
      steps.push({ sourceId: step.sourceId, sourceKind: 'account', amount: take });
      remaining -= take;
    } else {
      const td = tdsById.get(step.sourceId);
      if (!td) continue;
      const have = Math.max(0, availableByTd.get(step.sourceId) ?? 0);
      if (have <= 0) continue;
      const take = Math.min(have, remaining);
      availableByTd.set(step.sourceId, have - take);
      steps.push({
        sourceId: step.sourceId,
        sourceKind: 'term-deposit',
        amount: take,
        brokenEarlyMaturity: td.maturityDate,
        brokenEarlyLostInterest: estimateLostInterest(td, ym),
      });
      remaining -= take;
    }
  }
  return { steps, uncovered: Math.max(0, remaining) };
}

/** Rough estimate: rate * principal * (months-remaining-to-maturity / 12).
 *  Only used to populate the `td-broken-early` alert; not meant to be
 *  bank-grade accurate. */
function estimateLostInterest(td: CashflowTermDeposit, breakYm: string): number {
  const bParts = breakYm.split('-').map((s) => parseInt(s, 10));
  const bY = bParts[0] ?? 0;
  const bM = bParts[1] ?? 1;
  const matD = new Date(td.maturityDate);
  if (!isFinite(matD.getTime())) return 0;
  const matY = matD.getFullYear();
  const matM = matD.getMonth() + 1;
  const monthsRemaining = (matY - bY) * 12 + (matM - bM);
  if (monthsRemaining <= 0) return 0;
  return (td.rateAnnual / 100) * td.principal * (monthsRemaining / 12);
}
